import axios from "axios";
import fileDownload from "js-file-download";
import { CloudDownload, FormatListBulleted } from "@mui/icons-material";
import { useState } from "react";
import TextBlock from "Components/Layout/TextBlock";
import ListItem from "Components/ListItem";
import ListItemAction from "Components/ListItemAction";
import ListItemActions from "Components/ListItemActions";
import ListItemChip from "Components/ListItemChip";
import ListItemChips from "Components/ListItemChips";
import ListItemContent from "Components/ListItemContent";
import { ThemeColor, UserReport, UserReportStatus, UserReportType } from "../../system/Domain";
import formatRelativeDate from "../../system/formatRelativeDate";
import parseContentDispositionFilename from "../../system/parseContentDispositionFilename";
import UserReportParameterChips from "./UserReportParameterChips";
import UserReportParameterList from "./UserReportParameterList";


export const supportedUserReportTypes: UserReportType[] = [
	"CarmarketPreparation",
	"ValidationSlaExport",
	"VehiclesExport",
	"TransportOrdersExport",
	"AssessmentOrdersExport",
	"BcaSoldSalesExport",
];

export const formatUserReportType = (type: UserReportType) => {
	switch (type) {
		case "CarmarketPreparation":
			return "Carmarket Auktionsvorbereitung";
		case "ValidationSlaExport":
			return "Validation SLA Export";
		case "VehiclesExport":
			return "Fahrzeuge Excel-Export";
		case "TransportOrdersExport":
			return "Aufträge Excel-Export";
		case "AssessmentOrdersExport":
			return "Gutachten Excel-Export";
		case "BcaSoldSalesExport":
			return "BCA Verkäufe vom Vortag";
		default:
			return type;
	}
};

export const formatUserReport = (userReport: UserReport) => formatUserReportType(userReport.type);

export const formatUserReportStatus = (status: UserReportStatus) => {
	switch (status) {
		case "Pending":
			return "Ausstehend";
		case "Processing":
			return "Wird erstellt";
		case "Completed":
			return "Fertig";
		case "Failed":
			return "Fehler";
		default:
			return status;
	}
};

export const paintUserReportStatus = (status: UserReportStatus): ThemeColor => {
	switch (status) {
		case "Pending":
		case "Processing":
			return "primary";
		case "Completed":
			return "success";
		case "Failed":
			return "error";
		default:
			return "info";
	}
};

const downloadUserReportFile = async (hash?: string) => {
	if (!hash) {
		return;
	}

	const response = await axios.get(`/api/storage/${hash}`, {
		responseType: "blob",
	});

	const filename = parseContentDispositionFilename(response.headers["content-disposition"]);
	fileDownload(response.data, filename);
};

interface Props {
	userReport: UserReport;
}

type Mode = "view" | "parameters";

export default ({ userReport }: Props) => {
	const isDownloadable = userReport.status === "Completed" && !!userReport.file?.hash;
	const [mode, setMode] = useState<Mode>("view");

	return (
		<ListItem color={userReport.status === "Failed" ? "error" : "info"}>
			<ListItemActions>
				{isDownloadable && (
					<ListItemAction
						icon={<CloudDownload />}
						tooltip="Datei herunterladen"
						onClick={() => downloadUserReportFile(userReport.file?.hash)}
						color="inherit"
					/>
				)}
				<ListItemAction
					icon={<FormatListBulleted />}
					tooltip="Parameter anzeigen"
					onClick={() => setMode((m) => (m === "parameters" ? "view" : "parameters"))}
					color={mode === "parameters" ? "primary" : "inherit"}
				/>
			</ListItemActions>
			<ListItemContent>
				<TextBlock
					flat
					primary={formatUserReport(userReport)}
					secondary={`Angefordert ${formatRelativeDate(userReport.dateCreated)} von ${userReport.createdBy?.name ?? "Unbekannt"}`}
				/>
				{mode === "parameters" && <UserReportParameterList userReport={userReport} />}
				<ListItemChips>
					<ListItemChip
						label={formatUserReportStatus(userReport.status)}
						color={paintUserReportStatus(userReport.status)}
					/>
					{userReport.entries > 0 && <ListItemChip label={`${userReport.entries} Einträge`} />}
					<UserReportParameterChips userReport={userReport} onParameterClick={() => setMode("parameters")} />
					{userReport.status === "Failed" && userReport.error && (
						<ListItemChip label={userReport.error} color="error" />
					)}
				</ListItemChips>
			</ListItemContent>
		</ListItem>
	);
};
